import { FolderKanban, Users, Calendar } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

interface ProjectCardProps {
  project: {
    id: string;
    name: string;
    description: string;
    status: 'active' | 'completed' | 'on_hold' | 'archived';
    target_date: string | null;
    member_count?: number;
    task_count?: number;
  };
  onClick?: () => void;
  className?: string;
}

const STATUS_CONFIG: Record<string, { label: string; variant: 'default' | 'success' | 'warning' | 'secondary' }> = {
  active: { label: 'Active', variant: 'default' },
  completed: { label: 'Completed', variant: 'success' },
  on_hold: { label: 'On Hold', variant: 'warning' },
  archived: { label: 'Archived', variant: 'secondary' },
};

export function ProjectCard({ project, onClick, className }: ProjectCardProps) {
  const statusConfig = STATUS_CONFIG[project.status] ?? { label: 'Active', variant: 'default' as const };

  return (
    <Card
      className={cn('cursor-pointer transition-colors hover:border-primary/50', className)}
      onClick={onClick}
    >
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <FolderKanban className="h-4 w-4 text-muted-foreground" />
            <CardTitle className="text-base">{project.name}</CardTitle>
          </div>
          <Badge variant={statusConfig.variant}>{statusConfig.label}</Badge>
        </div>
      </CardHeader>
      <CardContent>
        <p className="text-sm text-muted-foreground line-clamp-2">{project.description}</p>
        <div className="mt-3 flex items-center gap-4 text-xs text-muted-foreground">
          {project.member_count !== undefined && (
            <span className="flex items-center gap-1">
              <Users className="h-3 w-3" />
              {project.member_count}
            </span>
          )}
          {project.task_count !== undefined && (
            <span>{project.task_count} tasks</span>
          )}
          {project.target_date && (
            <span className="flex items-center gap-1">
              <Calendar className="h-3 w-3" />
              {new Date(project.target_date).toLocaleDateString()}
            </span>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
